import React, { useState, useEffect } from 'react';

const MoodReactiveAvatar = ({ 
  mood = 'wise', 
  isPlaying = false,
  size = 'large',
  showParticles = false 
}) => {
  const [particles, setParticles] = useState([]);

  // Generate particles for ambient effects
  useEffect(() => {
    if (showParticles) {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 3 + Math.random() * 2,
      }));
      setParticles(newParticles);
    }
  }, [showParticles]);

  // Mood configurations
  const moodConfig = {
    wise: {
      borderColor: '#edc161', // Sacred gold
      glowColor: 'rgba(237, 193, 97, 0.3)',
      particleColor: '#edc161',
      filter: 'brightness(1.05) contrast(1.05)',
      description: 'Calm and wise presence',
    },
    serious: {
      borderColor: '#78716c', // Stone gray
      glowColor: 'rgba(120, 113, 108, 0.3)',
      particleColor: '#78716c',
      filter: 'brightness(0.95) contrast(1.1) saturate(0.8)',
      description: 'Serious and grounding',
    },
    mysterious: {
      borderColor: '#9b7de8', // Cosmic purple
      glowColor: 'rgba(155, 125, 232, 0.4)',
      particleColor: '#9b7de8',
      filter: 'brightness(0.9) contrast(1.15) hue-rotate(10deg)',
      description: 'Mysterious and intense',
    },
    joyful: {
      borderColor: '#10b981', // Green
      glowColor: 'rgba(16, 185, 129, 0.3)',
      particleColor: '#10b981',
      filter: 'brightness(1.1) contrast(1.05) saturate(1.2)',
      description: 'Joyful and optimistic',
    },
    warning: {
      borderColor: '#f59e0b', // Amber
      glowColor: 'rgba(245, 158, 11, 0.4)',
      particleColor: '#f59e0b',
      filter: 'brightness(1.0) contrast(1.1)',
      description: 'Careful attention needed',
    },
  };

  const config = moodConfig[mood] || moodConfig.wise;

  const sizeClasses = {
    small: 'w-24 h-24',
    medium: 'w-32 h-32',
    large: 'w-40 h-40',
    xlarge: 'w-48 h-48',
  };

  return (
    <div className="relative flex flex-col items-center">
      {/* Avatar container */}
      <div className="relative">
        {/* Outer glow ring (when playing) */}
        {isPlaying && (
          <div
            className="absolute inset-0 rounded-full animate-pulse"
            style={{
              border: `4px solid ${config.borderColor}`,
              boxShadow: `0 0 30px ${config.glowColor}`,
            }}
          />
        )}

        {/* Avatar image */}
        <div
          className={`${sizeClasses[size]} rounded-full overflow-hidden shadow-cosmic transition-all duration-500`}
          style={{
            filter: config.filter,
            boxShadow: `0 0 20px ${config.glowColor}`,
          }}
        >
          <img
            src="/sage-final.png"
            alt="Sage Varta"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Particles */}
        {showParticles && (
          <div className="absolute inset-0 pointer-events-none">
            {particles.map((particle) => (
              <div
                key={particle.id}
                className="absolute w-1 h-1 rounded-full animate-pulse"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  backgroundColor: config.particleColor,
                  animationDelay: `${particle.delay}s`,
                  animationDuration: `${particle.duration}s`,
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
        )}

        {/* Energy ripple effect (when playing) */}
        {isPlaying && (
          <div className="absolute inset-0 rounded-full">
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                backgroundColor: config.borderColor,
                opacity: 0.2,
              }}
            />
          </div>
        )}
      </div>

      {/* Mood indicator */}
      <div className="mt-4 text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
          style={{
            backgroundColor: config.glowColor,
            color: config.borderColor,
          }}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: config.borderColor }}
          />
          {config.description}
        </div>
      </div>
    </div>
  );
};

export default MoodReactiveAvatar;